'use client';

import { useEffect, useSyncExternalStore } from 'react';
import type { ShellNames } from './loading-chrome';

/**
 * The names the shell holds (`AppShell`'s views and groups), for a surface
 * below the pages that names a route the way the loading chrome does
 * (`knownTitleFor`): a route's error screen, which fills the top bar's slot
 * with that title (src/app/error-screens.tsx, `PageError`).
 *
 * A module store, as the tap's is (tap-mount-store.ts), never a context: the
 * shell sits above every page, and a context value there that changes reaches
 * streamed boundaries that have not hydrated yet (ui-kit.md, 2026-09-27). The
 * shell writes it on the client only, after it mounts; the server and the
 * hydrating render read no names.
 */

const NONE: ShellNames = { views: [], groups: [] };
let names: ShellNames = NONE;
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The shell publishes its names whenever its props change. */
export function usePublishShellNames(next: ShellNames): void {
  useEffect(() => {
    names = next;
    for (const listener of listeners) listener();
  }, [next]);
}

/** The names the shell holds; none on the server and while hydrating. */
export function useShellNames(): ShellNames {
  return useSyncExternalStore(
    subscribe,
    () => names,
    () => NONE,
  );
}
